import ProjectDeviceTypeClientListingPage from '@admin/components/projects/ProjectDeviceTypeClientListing';
import * as enabledApi from '@shared/api/enabledPlayApi';
import Button from '@shared/components/core/button/CoreButton';
import DescriptionEditor from '@shared/components/core/field/editor/DescriptionEditor';
import SubSectionRow from '@shared/components/core/headers/SubSectionRow';
import { FlexPageContent, SubSectionContent } from '@shared/components/core/layouts/CommonLayoutStyles';
import SplashLoader from '@shared/components/loading/SplashLoader';
import { convertDeviceTypeClientListingToUpdateRequest } from '@shared/hooks/helpers/projectUtils';
import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import { useEditDeviceTypeStore } from '@shared/hooks/state/EditDeviceTypeContainer';
import { useOrganizationStore } from '@shared/hooks/state/OrganizationContainer';
import useCurrentOrgDetailsFetching from '@shared/hooks/useCurrentOrgDetailsFetching';
import { useCurrentOrgDeviceTypeEditor } from '@shared/hooks/useCurrentOrgDeviceTypeEditor';
import useCurrentOrgProjectsFetching from '@shared/hooks/useCurrentOrgProjectsFetching';
import DeviceTypeThrottleRules, { getDefaultThrottleRules } from '@shared/models/devices/DeviceTypeThrottleRules';
import UpdateDeviceTypeListingsModel from '@shared/models/devices/UpdateDeviceTypeListingsModel';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import OrgNoAccessBanner from '../../banners/OrgNoAccessBanner';
import ProjectsUpgradeBanner from '../ProjectsUpgradeBanner';
import DeviceTypeNameEditor from './editors/DeviceTypeNameEditor';
import DeviceTypeSupportFeaturesEditor from './editors/DeviceTypeSupportFeaturesEditor';
import DeviceTypeThrottleRulesEditor from './editors/DeviceTypeThrottleRulesEditor';
import DeviceTypeToggleEditor from './editors/DeviceTypeTogglesEditor';

export default function ProjectDeviceTypePageContent() {
    const { t } = useTranslation();
    const { organizationId, deviceId } = useParams();
    const { currentOrg, canCreateAuthClients, isLoading } = useCurrentOrgDetailsFetching();
    const { currentOrgDeviceType, error, isLoading: isLoadingProject } = useCurrentOrgProjectsFetching();
    const { handleSave: handleSaveDeviceType } = useCurrentOrgDeviceTypeEditor(currentOrgDeviceType);
    const editDeviceType = useEditDeviceTypeStore((state) => state.editDeviceType);
    const { handleInputChange } = useEditDeviceTypeStore.getState();
    const hasChanges = useEditDeviceTypeStore((state) => state.hasChanges);
    const listingHasChanges = useEditDeviceTypeStore((state) => state.listingHasChanges);
    const editListing = useEditDeviceTypeStore((state) => state.editListing);
    const [isSaving, setIsSaving] = useState(false);
    const navigate = useNavigate();
    const [throttleRules, setThrottleRules] = useState<DeviceTypeThrottleRules | undefined>(
        currentOrgDeviceType?.commandThrottleRules
    );
    const [throttleRulesChanged, setThrottleRulesChanged] = useState(false);

    // Sync throttle rules when currentOrgDeviceType changes
    useEffect(() => {
        setThrottleRules(currentOrgDeviceType?.commandThrottleRules);
        setThrottleRulesChanged(false);
    }, [currentOrgDeviceType]);

    const handleThrottleRulesUpdate = (rules: DeviceTypeThrottleRules) => {
        setThrottleRules(rules);
        setThrottleRulesChanged(true);
    };

    const handleUnifiedSave = async () => {
        if (!organizationId || !deviceId) return;

        setIsSaving(true);
        let deviceTypeSaveSuccess = true;
        let listingSaveSuccess = true;
        let throttleRulesSaveSuccess = true;

        try {
            // Save device type if it has changes
            if (hasChanges) {
                const deviceTypeResult = await handleSaveDeviceType();
                deviceTypeSaveSuccess = deviceTypeResult.resultType === 'Ok';
            }

            // Save throttle rules if they have changes
            if (throttleRulesChanged) {
                const rulesPayload = throttleRules ?? getDefaultThrottleRules();
                const throttleRulesResult = await useOrganizationStore
                    .getState()
                    .updateOrgDeviceTypeThrottleRules(organizationId, deviceId, rulesPayload);
                throttleRulesSaveSuccess = throttleRulesResult.resultType === 'Ok';
                if (throttleRulesSaveSuccess) {
                    setThrottleRulesChanged(false);
                }
            }

            // Save client listings if they have changes
            if (listingHasChanges && editListing) {
                // Helper to validate URL format
                const isValidUrl = (url: string | undefined, isOptional: boolean = false): boolean => {
                    if (!url || url.trim() === '') return isOptional;
                    return url.startsWith('http://') || url.startsWith('https://');
                };

                // Helper to check if a listing is complete
                const isListingComplete = (listing: (typeof editListing)[0]): boolean => {
                    const anyHasOnboarding = editListing.some(
                        (l) => l.markdownOnboardingInstructions && l.markdownOnboardingInstructions.length > 1
                    );
                    const anyHasTroubleshooting = editListing.some(
                        (l) => l.markdownTroubleshootingInstructions && l.markdownTroubleshootingInstructions.length > 1
                    );

                    const requiredFields = [
                        listing.title,
                        listing.shortDescription,
                        isValidUrl(listing.iconUrl), // Validate URL format
                        isValidUrl(listing.darkModeIconUrl), // Validate URL format
                        (listing.markdownDescription && listing.markdownDescription.length > 25) ||
                            (listing.externalLink && listing.externalLink.trim() !== ''),
                    ];

                    // Validate optional external link if provided
                    if (listing.externalLink && listing.externalLink.trim() !== '') {
                        requiredFields.push(isValidUrl(listing.externalLink, true));
                    }

                    if (anyHasOnboarding) {
                        requiredFields.push(
                            listing.markdownOnboardingInstructions && listing.markdownOnboardingInstructions.length > 25
                        );
                    }
                    if (anyHasTroubleshooting) {
                        requiredFields.push(
                            listing.markdownTroubleshootingInstructions &&
                                listing.markdownTroubleshootingInstructions.length > 25
                        );
                    }

                    return requiredFields.every(Boolean);
                };

                // Filter to only complete listings
                const completeListings = editListing.filter(isListingComplete);
                const incompleteListings = editListing.filter((listing) => !isListingComplete(listing));

                if (completeListings.length === 0) {
                    useAlertsStore
                        .getState()
                        .showError(
                            'All locales are incomplete. Please complete at least one locale to 100% before saving.'
                        );
                    listingSaveSuccess = false;
                } else {
                    // Create en-us as a copy of en if en exists
                    let listingsToSave = [...completeListings];
                    const enListing = completeListings.find((l) => l.locale === 'en');
                    if (enListing) {
                        // Check if en-us already exists in complete listings
                        const hasEnUs = completeListings.some((l) => l.locale === 'en-us');
                        if (!hasEnUs) {
                            // Create en-us as a copy of en
                            const enUsListing = { ...enListing, locale: 'en-us' };
                            listingsToSave.push(enUsListing);
                        }
                    }

                    const request: UpdateDeviceTypeListingsModel = {
                        listings: convertDeviceTypeClientListingToUpdateRequest(listingsToSave),
                    };

                    // Use PUT for both create and update (upsert)
                    const response = await enabledApi.updateOrgDeviceTypeClientListing(
                        organizationId,
                        deviceId,
                        request
                    );

                    listingSaveSuccess = response.resultType === 'Ok';
                    if (listingSaveSuccess) {
                        useEditDeviceTypeStore.getState().setListingHasChanges(false);
                        // Reload the listings
                        const refreshResult = await enabledApi.getOrgDeviceTypeClientListings(organizationId, deviceId);
                        if (refreshResult.resultType === 'Ok' && refreshResult.data) {
                            // The EditDeviceTypeContainer will handle updating editListing
                        }

                        // Show info about incomplete locales
                        if (incompleteListings.length > 0) {
                            // Filter out en-us from incomplete list if it was auto-generated
                            const enListing = completeListings.find((l) => l.locale === 'en');
                            const hasEnUs = completeListings.some((l) => l.locale === 'en-us');
                            const supportedLocaleCodes = ['en', 'es', 'fr', 'it', 'de', 'ja'];
                            const actuallyIncompleteListings = incompleteListings.filter(
                                (l) =>
                                    !(l.locale === 'en-us' && enListing && !hasEnUs) &&
                                    supportedLocaleCodes.includes(l.locale)
                            );

                            if (actuallyIncompleteListings.length > 0) {
                                const incompleteLocales = actuallyIncompleteListings.map((l) => l.locale).join(', ');
                                useAlertsStore
                                    .getState()
                                    .showSuccess(
                                        `Saved successfully. Incomplete locales not saved: ${incompleteLocales.toUpperCase()}`
                                    );
                                return; // Early return to avoid the generic success message
                            }
                        }
                    }
                }
            }

            // Show appropriate success/error message
            if (deviceTypeSaveSuccess && listingSaveSuccess && throttleRulesSaveSuccess) {
                useAlertsStore.getState().showSuccess(t('common:saved'));
            } else {
                useAlertsStore.getState().showError(t('common:saveError'));
            }
        } catch (error) {
            console.error('Error saving:', error);
            useAlertsStore.getState().showError(t('common:saveError'));
        } finally {
            setIsSaving(false);
        }
    };

    const hasAnyChanges = hasChanges || listingHasChanges || throttleRulesChanged;
    if (!organizationId || !deviceId) return null;
    if (!currentOrgDeviceType || !editDeviceType || isLoading || isLoadingProject) return <SplashLoader />;
    if (!currentOrg || !canCreateAuthClients || error.device) return <OrgNoAccessBanner />;
    if (!currentOrg.isDeveloperAccess || !currentOrg.canCreateAuthClients) return <ProjectsUpgradeBanner />;

    return (
        <FlexPageContent>
            <SubSectionRow title={t('common:settings.title')} />
            <SubSectionContent>
                <DeviceTypeNameEditor
                    value={editDeviceType.name}
                    onUpdate={(field, value) => handleInputChange(field, value)}
                />
                <DescriptionEditor
                    value={editDeviceType.description}
                    onUpdate={(field, value) => handleInputChange(field, value)}
                />
                <DeviceTypeSupportFeaturesEditor editDeviceType={editDeviceType} onUpdate={handleInputChange} />
                <DeviceTypeToggleEditor editDeviceType={editDeviceType} onUpdate={handleInputChange} />
                <DeviceTypeThrottleRulesEditor throttleRules={throttleRules} onUpdate={handleThrottleRulesUpdate} />
            </SubSectionContent>
            {/* <SubSectionRow title={t('common:localization.titleShort')} /> */}
            {/* <SubSectionContent>
                <DeviceTypeDefaultLanguageEditor
                    value={editDeviceType.defaultLocale ?? 'en'}
                    supportsLocales={editDeviceType.supportsLocales ?? ['en']}
                    onUpdate={handleInputChange}
                />
                <DeviceTypeSupportsLocalesEditor
                    value={editDeviceType.supportsLocales ?? ['en']}
                    onUpdate={(field, value) => handleInputChange(field, value)}
                />
            </SubSectionContent> */}
            <ProjectDeviceTypeClientListingPage hideSaveButton />
            <SubSectionRow
                title={t('common:organization.projects.deviceType.customControls.title')}
                description={t('common:organization.projects.deviceType.customControls.description')}>
                <Button $color="secondary" startIcon="edit" onClick={() => navigate(`customControls`)}>
                    {t('common:edit')}
                </Button>
            </SubSectionRow>
            <Button onClick={handleUnifiedSave} disabled={!hasAnyChanges || isSaving}>
                {isSaving ? t('common:saving') : t('common:save')}
            </Button>
        </FlexPageContent>
    );
}
